import Decimal from 'decimal.js';

// TODO: TYPE-SAFETY - Parameter 'value' has implicit 'any' type. Should add explicit type annotation for better type safety
export const isNullOrUndefined = (value) => value === null || value === undefined;

/**
 * Performs a deep copy of an object without using JSON methods.
 * Handles primitive types, arrays, objects, dates, circular references, Decimal.js objects, Maps, and nested structures.
 * @template T The type of the object to copy
 * @param source The object to copy
 * @returns A deep copy of the source object
 */
export const deepCopy = <T>(source: T): T => {
    // Use a WeakMap to track objects that have already been copied to handle circular references
    const references = new WeakMap<object, unknown>();

    const copy = <U>(value: U): U => {
        // Handle null or undefined
        if (value === null || value === undefined) {
            return value;
        }

        // Handle primitive types (number, string, boolean)
        if (typeof value !== 'object') {
            return value;
        }

        // Handle Date objects
        if (value instanceof Date) {
            return new Date(value.getTime()) as unknown as U;
        }

        // Handle Decimal.js objects
        if (value instanceof Decimal) {
            return new Decimal(value) as unknown as U;
        }

        // Handle Map objects
        if (value instanceof Map) {
            const copiedMap = new Map();
            // Store reference before recursively copying elements to handle circular maps
            references.set(value as object, copiedMap);

            (value as Map<unknown, unknown>).forEach((mapValue, mapKey) => {
                copiedMap.set(copy(mapKey), copy(mapValue));
            });

            return copiedMap as unknown as U;
        }

        // Check if this object has already been copied (circular reference)
        if (references.has(value as object)) {
            return references.get(value as object) as U;
        }

        // Handle arrays
        if (Array.isArray(value)) {
            const copiedArray: unknown[] = [];
            // Store reference before recursively copying elements to handle circular arrays
            references.set(value as object, copiedArray);

            for (let i = 0; i < value.length; i++) {
                copiedArray[i] = copy(value[i]);
            }
            return copiedArray as unknown as U;
        }

        // Handle regular objects
        const result = {} as Record<string, unknown>;
        // Store reference before recursively copying properties to handle circular objects
        references.set(value as object, result);

        for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
                result[key] = copy((value as Record<string, unknown>)[key]);
            }
        }

        return result as unknown as U;
    };

    return copy<T>(source);
};

/**
 * Checks if a value is an object (not null, not an array, not a date)
 * @param value The value to check
 * @returns True if the value is an object
 */
export const isPlainObject = (value: unknown): boolean => {
    return value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
};
